
import { Capacitor } from '@capacitor/core';
import { t, Trans } from '@lingui/macro';
import { GenericResponse, RecordingData, VoiceRecorder } from 'capacitor-voice-recorder';
import EventEmitter from 'events';
import { Mic, Paperclip } from 'lucide-react';
import { useEffect, useReducer, useState } from 'react';
import { createPortal } from "react-dom";
import { Util } from '../../../services/Utils/Util';
import { AudioPlayer } from '../AudioPlayer';
import useDefaultDevices from '../useDefaultDevices';
import { Button } from "../../../components/ui/button";
import { Progress } from "../../../components/ui/progress";
import { useToast } from "../../../components/ui/use-toast";
import { cn } from "../../../lib/utils";

const MAX_LENGTH_SEC = 30;
export type RecordingAction = 'send' | 'attach' | 'cancel';
interface IMsgRecordModal {
  onAction: (action: RecordingAction, data: RecordingData | undefined) => void
};

const MsgRecordModal: React.FC<IMsgRecordModal> = ({ onAction }) => {
  const [state, setState] = useState<'ready' | 'recording' | 'uploading' | 'done'>('ready')
  const [isPlaying, setIsPlaying] = useState(false);
  const [show, setShow] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [player] = useState(new AudioPlayer(new Audio()));
  const [data, setData] = useState<RecordingData>();
  const [currentTime] = useState({ value: 0, active: true });
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const { toast } = useToast();
  const { audioInputDevice } = useDefaultDevices();
  const [footerDimensions, setFooterDimensions] = useState<{ width: number; left: number } | null>(null);

  let seconds = currentTime.value;
  let time = new Date(seconds * 1000);
  let timeStr = time.toTimeString().split(' ')[0];
  let timeValue = timeStr.slice(3, 8);

  useEffect(() => {
    VoiceRecordModalEmitter.subscribe(() => showAndStartRecording());
  }, [])

  // Footer 너비 측정 및 화면 크기 변경 감지
  useEffect(() => {
    const updateDimensions = () => {
      const footer = document.querySelector('#msg-input-footer-container');
      if (footer) {
        const rect = footer.getBoundingClientRect();
        setFooterDimensions({
          width: rect.width,
          left: rect.left
        });
      }
    };

    if (show) {
      updateDimensions();
      window.addEventListener('resize', updateDimensions);
      return () => window.removeEventListener('resize', updateDimensions);
    }
  }, [show]);

  // ESC 키로 닫기
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && show) {
        handleClose();
      }
    };

    if (show) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [show]);

  async function didDismiss() {
    currentTime.active = false;
    let status = await VoiceRecorder.getCurrentStatus()
    if (status.status == 'RECORDING') {
      await VoiceRecorder.stopRecording();
    }
    if (isPlaying) {
      player.stop();
    }
    setState('ready');
  }

  const handleClose = () => {
    setIsClosing(true);
    didDismiss();
    setTimeout(() => {
      setShow(false);
      setIsClosing(false);
    }, 300);
  };

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 bg-black/50 z-[1900]",
          isClosing ? "animate-out fade-out-0 duration-300" : "animate-in fade-in-0"
        )}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Bottom Sheet */}
      <div
        className={cn(
          "fixed bottom-0 z-[2000] bg-background rounded-t-xl border-t border-l border-r shadow-lg p-6",
          isClosing
            ? "animate-out slide-out-to-bottom duration-300"
            : "animate-in slide-in-from-bottom duration-300"
        )}
        style={footerDimensions ? {
          width: `${footerDimensions.width}px`,
          left: `${footerDimensions.left}px`
        } : undefined}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex flex-col gap-4">
          {state != 'ready' &&
            <div className='flex flex-col gap-4'>
              <div className='flex flex-col items-center gap-2'>
                <div className='flex items-center justify-between w-full'>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={throwAway}
                    className="h-8 w-8"
                    data-cy="cancel-record"
                  >
                    <img src='/assets/icon/ic-cancel-black-24.svg' alt="Cancel" className="w-6 h-6" />
                  </Button>

                  <div className='flex flex-col items-center'>
                    <span className="font-medium">
                      {state == 'recording' ? <Trans>Recording audio clip...</Trans> : <Trans>Recorded</Trans>}
                    </span>
                    {audioInputDevice?.label && (
                      <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                        <Mic className='h-3 w-3' />
                        {Util.getTextInsideParenthesis(audioInputDevice?.label)}
                      </div>
                    )}
                  </div>

                  <div className="w-8" /> {/* Spacer for centering */}
                </div>

                {state == 'recording' && (
                  <div className='w-full flex flex-col items-center gap-2'>
                    <Progress value={(currentTime.value / MAX_LENGTH_SEC) * 100} className="h-2" />
                    <span className='text-sm font-mono'>{timeValue}</span>
                  </div>
                )}

                {state == 'done' && (
                  <div className='w-full flex flex-col items-center gap-2'>
                    <div className="w-full flex items-center gap-2">
                      <Progress
                        value={(currentTime.value / ((data?.value.msDuration ?? 0) / 1000)) * 100}
                        className={cn("h-2 flex-1", isPlaying && "bg-primary/20")}
                      />
                      <span className='text-sm font-mono'>{timeValue}</span>
                    </div>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={isPlaying ? stopPlaying : playRecording}
                      className="h-10 w-10"
                    >
                      <img
                        src={isPlaying ? '/assets/icon/ic-pause-black-24.svg' : '/assets/icon/ic-play-black-24.svg'}
                        alt={isPlaying ? "Pause" : "Play"}
                        className="w-6 h-6"
                      />
                    </Button>
                  </div>
                )}
              </div>

              <div className='flex items-center justify-center gap-4 mt-2'>
                {data && (
                  <Button variant="ghost" size="icon" onClick={closeAndAttach} className="h-10 w-10">
                    <Paperclip className="h-6 w-6 text-gray-500" />
                  </Button>
                )}

                {state == 'recording' && (
                  <Button variant="ghost" size="icon" onClick={stopRecording} className="h-12 w-12 rounded-full bg-red-100 hover:bg-red-200">
                    <img src='/assets/icon/ic-pause-black-24.svg' alt="Stop" className="w-6 h-6" />
                  </Button>
                )}

                {state == 'done' && (
                  <Button variant="ghost" size="icon" onClick={startRecording} disabled={isPlaying} className="h-10 w-10">
                    <img src='/assets/icon/ic-refresh-black-24.svg' alt="Retry" className="w-6 h-6" />
                  </Button>
                )}

                <div
                  onClick={() => sendButton()}
                  className="cursor-pointer rounded-full bg-green-500 p-3 shadow-lg hover:bg-green-600 transition-colors animate-pulse"
                >
                  <img src='/assets/icon/ic-send-white-24.svg' alt="Send" className="w-6 h-6" />
                </div>
              </div>

              {state == 'uploading' && <Progress value={undefined} className="h-1" />}
            </div>
          }
        </div>
      </div>
    </>
  );

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => showAndStartRecording()}
        className="h-8 w-8 rounded-full bg-[#f2f2f2] hover:bg-[#e5e5e5]"
        data-cy="recorder-btn"
      >
        <Mic className="h-5 w-5 text-[#939393]" />
      </Button>
      {show && createPortal(modalContent, document.body)}
    </>
  );

  async function showAndStartRecording() {
    setShow(true);
    setData(undefined);
    startRecording();
  }

  async function startRecording() {
    try {

      const result: GenericResponse = await VoiceRecorder.requestAudioRecordingPermission();
      if (!Capacitor.isNativePlatform() && !result.value) {
        toast({
          title: t`You need to allow audio recording permission to use this feature.`,
          duration: 3000,
        });
        setShow(false);
        return;
      }

      let res = await VoiceRecorder.startRecording();
      currentTime.value = 0;
      currentTime.active = true;
      setState('recording');
      setIsPlaying(false);
      setData(undefined);

      let recordingInterval = setInterval(() => {
        currentTime.value += 1;
        forceUpdate();
        if (currentTime.value >= MAX_LENGTH_SEC || !currentTime.active) {
          VoiceRecorder.getCurrentStatus().then((r) => {
            if (r.status == 'RECORDING') {
              stopRecording();
            }
          })
          clearInterval(recordingInterval);
        }
      }, 1000);
    } catch (error) {
      toast({
        title: t`Could not record. ${(error as Error).message}`,
        duration: 3000,
      });
    }
  }

  async function stopRecording(): Promise<RecordingData> {
    console.log('stop recording');
    let res = await VoiceRecorder.stopRecording();
    setData(res);
    setState('done');
    currentTime.active = false;
    return res;
  }

  async function playRecording() {
    if (data) {
      setIsPlaying(true);
      const delayMS = 100;
      currentTime.value = 0;
      currentTime.active = true;
      let recordingInterval = setInterval(() => {
        currentTime.value += delayMS / 1000;
        forceUpdate();
        if (currentTime.value >= ((data?.value.msDuration ?? 0) / 1000) || !currentTime.active) {
          clearInterval(recordingInterval);
        }
      }, delayMS);

      await player.playData(data);
      setIsPlaying(false);
    }
  }

  function stopPlaying() {
    player.stop();
    currentTime.active = false;
    setIsPlaying(false);
  }

  async function sendButton() {
    if (state == 'recording') {
      let _data = await stopRecording();
      sendData(_data);
    } else if (data) {
      sendData(data);
    }
  }

  async function sendData(recordingData: RecordingData) {
    if (recordingData) {
      onAction('send', recordingData)
      setState('uploading');
      setShow(false);
      toast({
        title: t`Sending voice message...`,
        duration: 500,
      });
    }
  }

  function closeAndAttach() {
    handleClose();
    onAction('attach', data)
  }

  function throwAway() {
    handleClose();
    onAction('cancel', undefined);
  }
};

export class VoiceRecordModalEmitter {
  private static emitter = new EventEmitter();

  static startRecording() {
    this.emitter.emit("start");
  }

  static subscribe(handler: () => void): () => void {
    this.emitter.on("start", handler);
    return () => this.emitter.removeAllListeners();
  }
}

export default MsgRecordModal;